import * as THREE from 'three'
import { DropInViewer } from '@mkkellogg/gaussian-splats-3d'

export type StageId = 'gate' | 'grounds' | 'interior'

interface StageDef {
  ply: string
  meta: string
  /** ステージの目標幅（m） */
  targetWidth: number
  /** シーン手前端を置く位置（m, -zが前方） */
  nearEdgeZ: number
  /** シーン底面の高さ（m） */
  bottomY: number
}

interface ScenesManifest {
  stages: Record<StageId, StageDef>
}

interface LoadedViewer {
  holder: THREE.Group
  /** X軸π回転後のbbox */
  tMin: THREE.Vector3
  tMax: THREE.Vector3
  count: number
}

/**
 * 3DGS環境（3ステージ: 表門 / 移動用=敷地 / 一畳敷内部）。論文の3対象に対応。
 * 描画ライブラリ（GaussianSplats3D DropInViewer）はここに閉じ込める。
 *
 * 同一PLYを複数ステージが参照する場合はビューアを共有し、ステージ切替は
 * 配置トランスフォームの適用のみ（メモリ節約）。実データ導入時は scenes.json の
 * ply をステージごとに差し替えれば、ステージごとに個別ロードされる。
 *
 * サンプルPLYは COLMAP系（y下向き）なので X軸π回転で three.js（y上向き）へ変換し、
 * scene_meta.json の bbox（2-98パーセンタイル）で正規化する。
 */
export class SplatEnvironment {
  readonly root = new THREE.Group()
  splatCount = 0
  currentStage: StageId | null = null

  private viewers = new Map<string, LoadedViewer>()
  private stageDefs = new Map<StageId, StageDef>()

  async load(manifestUrl: string): Promise<void> {
    const manifest: ScenesManifest = await fetch(manifestUrl).then((r) => r.json())
    const crossOriginIsolated =
      typeof self !== 'undefined' &&
      (self as unknown as { crossOriginIsolated?: boolean }).crossOriginIsolated === true

    const base = manifestUrl.slice(0, manifestUrl.lastIndexOf('/') + 1)
    for (const [id, def] of Object.entries(manifest.stages) as Array<[StageId, StageDef]>) {
      this.stageDefs.set(id, def)
      const plyUrl = base + def.ply
      if (this.viewers.has(plyUrl)) continue

      const meta = await fetch(base + def.meta).then((r) => r.json())
      const viewer = new DropInViewer({
        gpuAcceleratedSort: false,
        sharedMemoryForWorkers: crossOriginIsolated,
        integerBasedSort: true,
        freeIntermediateSplatData: true,
      })
      await viewer.addSplatScene(plyUrl, {
        splatAlphaRemovalThreshold: 5,
        showLoadingUI: false,
        progressiveLoad: false,
      })

      // COLMAP y下向き → three.js y上向き（X軸π回転）
      const flip = new THREE.Group()
      flip.rotation.x = Math.PI
      flip.add(viewer)
      const holder = new THREE.Group()
      holder.add(flip)
      holder.visible = false
      this.root.add(holder)

      const [minX, minY, minZ] = meta.bbox.min as number[]
      const [maxX, maxY, maxZ] = meta.bbox.max as number[]
      this.viewers.set(plyUrl, {
        holder,
        tMin: new THREE.Vector3(minX, -maxY, -maxZ),
        tMax: new THREE.Vector3(maxX, -minY, -minZ),
        count: meta.count as number,
      })
    }
  }

  /** 指定ステージを表示（null で全非表示）。切替はフェード中に呼ぶこと */
  showStage(id: StageId | null): void {
    for (const v of this.viewers.values()) v.holder.visible = false
    this.currentStage = id
    this.splatCount = 0
    if (!id) return

    const def = this.stageDefs.get(id)
    if (!def) return
    const plyUrl = this.resolvePlyUrl(def)
    const v = plyUrl ? this.viewers.get(plyUrl) : undefined
    if (!v) return

    const size = new THREE.Vector3().subVectors(v.tMax, v.tMin)
    const centerX = (v.tMin.x + v.tMax.x) / 2
    const footprint = Math.max(size.x, Math.abs(size.z), 1)
    const s = THREE.MathUtils.clamp(def.targetWidth / footprint, 0.1, 10)
    v.holder.scale.setScalar(s)
    v.holder.position.set(
      -centerX * s,
      -v.tMin.y * s + def.bottomY,
      def.nearEdgeZ - v.tMax.z * s,
    )
    v.holder.visible = true
    this.splatCount = v.count
  }

  private resolvePlyUrl(def: StageDef): string | undefined {
    for (const url of this.viewers.keys()) {
      if (url.endsWith('/' + def.ply) || url === def.ply) return url
    }
    return undefined
  }
}
